// domain/model/Degree.js
import { DEGREE_LEVELS, degreeLevelRank, isDegreeLevel } from '../constants/degree';

/**
 * A degree somebody was awarded.
 *
 * CONFERRED ONCE, NEVER EXPIRES, so it carries a date and not a window. That is what separates it
 * from a certification, which holds a validity period and is renewed, and it is why the two are
 * different records rather than one with a nullable end. Resolving as of a date therefore asks only
 * whether it had been conferred by then.
 *
 * THE FIELD OF STUDY IS NOT DECORATION. Some payers grant the bachelor's-level modifier only for a
 * bachelor's in a related field, and others grant it for any bachelor's, so the field is stored on
 * every degree even though only some rules read it. Which fields count as related is the rule's
 * business, not the degree's: it varies by payer, and by state manual for Medicaid.
 *
 * ACCREDITATION IS A GATE, NOT A LABEL. Rules are written as "from an accredited institution", so an
 * unaccredited degree is treated as if the person does not hold it. A degree awarded outside the US
 * passes that gate only through an equivalency evaluation stating the US level, which is why the
 * evaluation is part of the record rather than a note: it IS the proof, and an auditor reads it.
 *
 * @domainObject value-object
 * @typedef {Object} Degree
 * @property {string} level - a rung on `DEGREE_LEVELS`
 * @property {string|null} fieldOfStudy - as written on the award
 * @property {string|null} school
 * @property {string|null} conferredOn - 'YYYY-MM-DD'; null when nobody recorded it
 * @property {boolean} isAccredited
 * @property {DegreeEquivalency|null} equivalency - present only for a degree awarded outside the US
 * @property {string|null} proof - what is filed behind it, in the clinic's own words
 */

/**
 * @typedef {Object} DegreeEquivalency
 * @property {string|null} evaluatedBy - the service that issued the evaluation
 * @property {string|null} evaluatedOn - 'YYYY-MM-DD'
 * @property {string|null} equivalentLevel - the US rung the evaluation states
 */

const text = (value) => (typeof value === 'string' && value.trim().length > 0 ? value.trim() : null);

/**
 * Reads a stored entry as a Degree, or null when it names no rung.
 *
 * A row whose level is unreadable is dropped rather than carried with a null level: every question
 * asked of a degree is about its rung, so a degree without one answers nothing and would only travel
 * far enough to be mistaken for a qualification somebody holds.
 */
export function createDegree(entry) {
  if (!isDegreeLevel(entry?.level)) {
    return null;
  }

  const equivalency = entry?.equivalency ?? null;

  return {
    level: entry.level,
    fieldOfStudy: text(entry.fieldOfStudy),
    school: text(entry.school),
    conferredOn: text(entry.conferredOn),
    isAccredited: entry.isAccredited === true,
    equivalency: equivalency === null ? null : {
      evaluatedBy: text(equivalency.evaluatedBy),
      evaluatedOn: text(equivalency.evaluatedOn),
      equivalentLevel: isDegreeLevel(equivalency.equivalentLevel) ? equivalency.equivalentLevel : null,
    },
    proof: text(entry.proof),
  };
} // createDegree()

/**
 * The rung this degree counts as for a rule, or null when it counts as nothing.
 *
 * An unaccredited degree counts as nothing, which is the whole of the accreditation gate. A degree
 * carrying an equivalency counts as the level the evaluation states rather than the level claimed on
 * it, because that evaluation is the only thing a payer will accept as saying what it is worth here.
 */
export function creditedLevel(degree) {
  if (degree === null || degree === undefined || degree.isAccredited !== true) {
    return null;
  }

  return degree.equivalency?.equivalentLevel ?? degree.level;
} // creditedLevel()

/**
 * Had this degree been conferred by `asOf`?
 *
 * A degree with no conferral date recorded answers NO rather than yes. It is the same answer the
 * record itself gives: nobody can say when it started counting, so nothing may be billed on it for a
 * date in the past. The visible version of that is an incomplete row in the editor.
 */
export function isHeldOn(degree, asOf) {
  const conferredOn = degree?.conferredOn;

  if (typeof conferredOn !== 'string' || typeof asOf !== 'string' || asOf.length === 0) {
    return false;
  }

  return conferredOn <= asOf;
} // isHeldOn()

/**
 * The highest rung this person counted as holding on a date, or null for none.
 *
 * Null is the answer for somebody with no qualifying degree, and a rule reading the degree axis
 * treats it as below the lowest rung it asks about. It deliberately does NOT mean "unknown": a rule
 * that needs to tell those apart is asking a different question, and a person with nothing on file
 * and a person whose file is empty look the same to a payer.
 */
export function highestCreditedLevelOn(degrees, asOf) {
  return (degrees ?? []).reduce((best, degree) => {
    if (!isHeldOn(degree, asOf)) {
      return best;
    }

    const level = creditedLevel(degree);

    return degreeLevelRank(level) > degreeLevelRank(best) ? level : best;
  }, null);
} // highestCreditedLevelOn()

export { DEGREE_LEVELS };
